import { adminApiClient } from '@/services/admin/baseService'
import { getApiClientHeaderForMode } from '@/utils/apiConfig'
import { toBooleanValue } from '@/services/admin/valueHelpers'
import { extractArrayFromApiPayload } from '@/services/admin/paginatedResponseHelpers'
import type {
  ApiResponse,
  OnchainOperationPolicyCapability,
  OnchainOperationProfile,
  OnchainOperationPolicyPlanOverride,
  TronContractUsageSnapshot,
  TronFeeData,
  CreateOnchainOperationProfilePayload,
  UpdateOnchainOperationProfilePayload,
  UpdateOnchainOperationPolicyPlanOverridePayload,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = toNumberOrUndefined(value)
  return parsed === undefined ? null : parsed
}

export interface OnchainProfileListParams {
  chainId?: string
  operationType?: string
  isActive?: boolean
  limit?: number
}

export interface OnchainPolicyPlanOverrideListParams {
  planId?: string
  chainId?: string
  operationType?: string
  isActive?: boolean
  limit?: number
}

type ModeAwareRequestConfig = {
  headers: Record<string, string>
  __preserveAppClientHeader: boolean
}

const buildModeAwareConfig = (
  mode?: NetworkEnvironment
): ModeAwareRequestConfig | undefined => {
  if (!mode) {
    return undefined
  }
  return {
    headers: {
      'X-App-Client': getApiClientHeaderForMode(mode),
    },
    __preserveAppClientHeader: true,
  }
}

const toStringSafe = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof value === 'number') {
    return value.toString()
  }
  return undefined
}

const mapOnchainProfileRecord = (record: any): OnchainOperationProfile => {
  const parseJson = (value: unknown): unknown | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'object') return value as unknown
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    if (!trimmed) return null
    try {
      return JSON.parse(trimmed) as unknown
    } catch {
      return null
    }
  }

  return {
    id: toStringSafe(record?.id) ?? '',
    chainId: toStringSafe(record?.chainId ?? record?.chain_id) ?? '',
    chainName: toStringSafe(record?.chainName ?? record?.chain_name) ?? undefined,
    chainCode: toStringSafe(record?.chainCode ?? record?.chain_code) ?? undefined,
    compatibility: toStringSafe(record?.compatibility) ?? undefined,
    operationType: toStringSafe(record?.operationType ?? record?.operation_type) ?? '',
    isActive: toBooleanValue(record?.isActive ?? record?.is_active, false),
    minNetUsd: toNumberOrUndefined(record?.minNetUsd ?? record?.min_net_usd) ?? 0,
    costWeight: toNumberOrNull(record?.costWeight ?? record?.cost_weight),
    safetyFactor: toNumberOrNull(record?.safetyFactor ?? record?.safety_factor),
    spentUsdMultiplier: toNumberOrNull(record?.spentUsdMultiplier ?? record?.spent_usd_multiplier),
    bypassBudgetGate: toBooleanValue(record?.bypassBudgetGate ?? record?.bypass_budget_gate, false),
    kvTtlSeconds: toNumberOrNull(record?.kvTtlSeconds ?? record?.kv_ttl_seconds),
    kvSampleSize: toNumberOrNull(record?.kvSampleSize ?? record?.kv_sample_size),
    bootstrapGasUnitsP90: toNumberOrNull(record?.bootstrapGasUnitsP90 ?? record?.bootstrap_gas_units_p90),
    bootstrapTronEnergyP90: toNumberOrNull(record?.bootstrapTronEnergyP90 ?? record?.bootstrap_tron_energy_p90),
    bootstrapTronBandwidthP90: toNumberOrNull(
      record?.bootstrapTronBandwidthP90 ?? record?.bootstrap_tron_bandwidth_p90
    ),
    sweepConfig: parseJson(record?.sweepConfig ?? record?.sweep_config_json),
    solanaConfig: parseJson(record?.solanaConfig ?? record?.solana_config_json),
    tronTrxFallbackMaxTrx: toNumberOrNull(record?.tronTrxFallbackMaxTrx ?? record?.tron_trx_fallback_max_trx),
    notes: toStringSafe(record?.notes) ?? null,
    revision: toNumberOrUndefined(record?.revision) ?? 1,
    createdAt: toStringSafe(record?.createdAt ?? record?.created_at) ?? new Date().toISOString(),
    updatedAt: toStringSafe(record?.updatedAt ?? record?.updated_at) ?? new Date().toISOString(),
  }
}

const mapOnchainPlanOverrideRecord = (record: any): OnchainOperationPolicyPlanOverride => ({
  id: toStringSafe(record?.id) ?? '',
  planId: toStringSafe(record?.planId ?? record?.plan_id) ?? '',
  planName: toStringSafe(record?.planName ?? record?.plan_name) ?? undefined,
  chainId: toStringSafe(record?.chainId ?? record?.chain_id) ?? '',
  chainName: toStringSafe(record?.chainName ?? record?.chain_name) ?? undefined,
  chainCode: toStringSafe(record?.chainCode ?? record?.chain_code) ?? undefined,
  compatibility: toStringSafe(record?.compatibility) ?? undefined,
  operationType: toStringSafe(record?.operationType ?? record?.operation_type) ?? '',
  isActive: toBooleanValue(record?.isActive ?? record?.is_active, false),
  minNetUsdOverrideEnabled: toBooleanValue(
    record?.minNetUsdOverrideEnabled ?? record?.min_net_usd_override_enabled,
    false
  ),
  minNetUsd: toNumberOrNull(record?.minNetUsd ?? record?.min_net_usd),
  safetyFactorOverrideEnabled: toBooleanValue(
    record?.safetyFactorOverrideEnabled ?? record?.safety_factor_override_enabled,
    false
  ),
  safetyFactor: toNumberOrNull(record?.safetyFactor ?? record?.safety_factor),
  spentUsdMultiplierOverrideEnabled: toBooleanValue(
    record?.spentUsdMultiplierOverrideEnabled ?? record?.spent_usd_multiplier_override_enabled,
    false
  ),
  spentUsdMultiplier: toNumberOrNull(record?.spentUsdMultiplier ?? record?.spent_usd_multiplier),
  bypassBudgetGateOverrideEnabled: toBooleanValue(
    record?.bypassBudgetGateOverrideEnabled ?? record?.bypass_budget_gate_override_enabled,
    false
  ),
  bypassBudgetGate: toBooleanValue(record?.bypassBudgetGate ?? record?.bypass_budget_gate, false),
  budgetSubsidyUsd: toNumberOrNull(record?.budgetSubsidyUsd ?? record?.budget_subsidy_usd) ?? 0,
  notes: toStringSafe(record?.notes) ?? null,
  revision: toNumberOrUndefined(record?.revision) ?? 1,
  createdAt: toStringSafe(record?.createdAt ?? record?.created_at) ?? new Date().toISOString(),
  updatedAt: toStringSafe(record?.updatedAt ?? record?.updated_at) ?? new Date().toISOString(),
  effectivePolicyPreview: record?.effectivePolicyPreview ?? record?.effective_policy_preview ?? null,
  cacheRefreshStatus: record?.cacheRefreshStatus ?? record?.cache_refresh_status ?? null,
  auditEventId: toStringSafe(record?.auditEventId ?? record?.audit_event_id) ?? null,
})

const mapOnchainPlanOverrideMutationPayload = (payload: any): OnchainOperationPolicyPlanOverride | null => {
  const record = payload?.item ?? payload?.data ?? payload
  if (!record) {
    return null
  }
  return mapOnchainPlanOverrideRecord({
    ...record,
    effectivePolicyPreview: payload?.effectivePolicyPreview ?? record?.effectivePolicyPreview ?? null,
    cacheRefreshStatus: payload?.cacheRefreshStatus ?? record?.cacheRefreshStatus ?? null,
    auditEventId: payload?.auditEventId ?? record?.auditEventId ?? null,
  })
}

const buildPlanOverridePayload = (
  payload: UpdateOnchainOperationPolicyPlanOverridePayload
): Record<string, unknown> => {
  const body: Record<string, unknown> = {}
  const entries: Array<[keyof UpdateOnchainOperationPolicyPlanOverridePayload, string]> = [
    ['planId', 'plan_id'],
    ['chainId', 'chain_id'],
    ['operationType', 'operation_type'],
    ['isActive', 'is_active'],
    ['minNetUsdOverrideEnabled', 'min_net_usd_override_enabled'],
    ['minNetUsd', 'min_net_usd'],
    ['safetyFactorOverrideEnabled', 'safety_factor_override_enabled'],
    ['safetyFactor', 'safety_factor'],
    ['spentUsdMultiplierOverrideEnabled', 'spent_usd_multiplier_override_enabled'],
    ['spentUsdMultiplier', 'spent_usd_multiplier'],
    ['bypassBudgetGateOverrideEnabled', 'bypass_budget_gate_override_enabled'],
    ['bypassBudgetGate', 'bypass_budget_gate'],
    ['budgetSubsidyUsd', 'budget_subsidy_usd'],
    ['notes', 'notes'],
    ['expectedRevision', 'expected_revision'],
  ]

  for (const [source, target] of entries) {
    const value = payload[source]
    if (value !== undefined) {
      body[target] = value
    }
  }
  return body
}

export const economyService = {
  async getEconomyStatus(mode?: NetworkEnvironment): Promise<any> {
    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        '/admin/economy/status',
        buildModeAwareConfig(mode)
      )

      if (response?.data?.data) {
        return response.data.data
      }

      throw new Error('Economy status API returned no data')
    } catch (error) {
      console.error('Error fetching economy status:', error)
      const apiMessage =
        (error as any)?.response?.data?.error?.message ??
        (error as any)?.response?.data?.message ??
        (error instanceof Error ? error.message : null)
      throw new Error(apiMessage || 'Failed to fetch economy status')
    }
  },

  async getTronFeesMonitor(mode?: NetworkEnvironment): Promise<{
    data: Record<string, TronFeeData>
    lastContractDeployment?: TronContractUsageSnapshot | null
    lastContractInteraction?: TronContractUsageSnapshot | null
  }> {
    const response = await adminApiClient.get<{
      status?: string
      data?: Record<string, TronFeeData>
      lastContractDeployment?: TronContractUsageSnapshot | null
      lastContractInteraction?: TronContractUsageSnapshot | null
    }>('/admin/monitor/tron-fees', buildModeAwareConfig(mode))

    return {
      data: response?.data?.data ?? {},
      lastContractDeployment: response?.data?.lastContractDeployment ?? null,
      lastContractInteraction: response?.data?.lastContractInteraction ?? null,
    }
  },

  async getGasPricesMonitor(mode?: NetworkEnvironment): Promise<Record<string, any>> {
    const response = await adminApiClient.get<{
      status?: string
      data?: Record<string, any>
    }>('/admin/monitor/gas-prices', buildModeAwareConfig(mode))

    return response?.data?.data ?? {}
  },

  async refreshTronFees(params?: {
    chainIds?: string[]
  }, mode?: NetworkEnvironment): Promise<{
    data: Record<string, TronFeeData>
    lastContractDeployment?: TronContractUsageSnapshot | null
    lastContractInteraction?: TronContractUsageSnapshot | null
  }> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    try {
      const response = await adminApiClient.post<
        ApiResponse<Record<string, TronFeeData>> & {
          lastContractDeployment?: TronContractUsageSnapshot | null
          lastContractInteraction?: TronContractUsageSnapshot | null
        }
      >(
        '/admin/economy/tron-fees/refresh',
        params || {},
        { signal: controller.signal, ...buildModeAwareConfig(mode) }
      )

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from Tron fee refresh API')
      }

      return {
        data: response.data.data,
        lastContractDeployment: response.data.lastContractDeployment ?? null,
        lastContractInteraction: response.data.lastContractInteraction ?? null,
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tron fee refresh timeout after 15 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async getOnchainOperationProfiles(
    params?: OnchainProfileListParams,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationProfile[]> {
    const searchParams = new URLSearchParams()
    if (params?.chainId) searchParams.append('chain_id', params.chainId)
    if (params?.operationType) searchParams.append('operation_type', params.operationType)
    if (params?.isActive !== undefined) searchParams.append('is_active', params.isActive ? 'true' : 'false')
    if (params?.limit) searchParams.append('limit', params.limit.toString())

    const query = searchParams.toString()
    const url = query
      ? `/admin/portal/onchain-operation-policies?${query}`
      : '/admin/portal/onchain-operation-policies'

    try {
      const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
      const payload = response.data
      const items = extractArrayFromApiPayload<any>(payload, {
        keys: ['items', 'profiles'],
        candidateKeys: ['items', 'records', 'deployments', 'history'],
        includePagination: true,
      })
      return items.map(mapOnchainProfileRecord)
    } catch (error) {
      console.error('Error fetching on-chain operation policies:', error)
      return []
    }
  },

  async getOnchainOperationProfile(
    profileId: string,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationProfile | null> {
    if (!profileId) {
      return null
    }

    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        `/admin/portal/onchain-operation-policies/${profileId}`,
        buildModeAwareConfig(mode)
      )
      const payload = response.data?.data ?? response.data
      const record = payload?.item ?? payload?.data ?? payload
      if (!record) {
        return null
      }
      return mapOnchainProfileRecord(record)
    } catch (error) {
      console.error(`Error fetching on-chain operation policy ${profileId}:`, error)
      return null
    }
  },

  async updateOnchainOperationProfile(
    profileId: string,
    payload: UpdateOnchainOperationProfilePayload,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationProfile | null> {
    const body: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined) {
        body[key] = value
      }
    }

    try {
      const response = await adminApiClient.put<ApiResponse<any>>(
        `/admin/portal/onchain-operation-policies/${profileId}`,
        body,
        buildModeAwareConfig(mode)
      )
      const payload = response.data?.data ?? response.data
      const record = payload?.item ?? payload?.data ?? payload
      if (!record) {
        return null
      }
      return mapOnchainProfileRecord(record)
    } catch (error) {
      console.error(`Error updating on-chain operation profile ${profileId}:`, error)
      throw error
    }
  },

  async createOnchainOperationProfile(
    payload: CreateOnchainOperationProfilePayload,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationProfile | null> {
    const body: Record<string, unknown> = {
      chain_id: payload.chainId,
      operation_type: payload.operationType,
    }
    for (const [key, value] of Object.entries(payload)) {
      if (
        key !== 'chainId'
        && key !== 'operationType'
        && key !== 'expectedRevision'
        && value !== undefined
      ) {
        body[key] = value
      }
    }
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/portal/onchain-operation-policies',
      body,
      buildModeAwareConfig(mode)
    )
    const responsePayload = response.data?.data ?? response.data
    const record = responsePayload?.item ?? responsePayload?.data ?? responsePayload
    return record ? mapOnchainProfileRecord(record) : null
  },

  async getOnchainOperationPolicyPlanOverrides(
    params?: OnchainPolicyPlanOverrideListParams,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyPlanOverride[]> {
    const searchParams = new URLSearchParams()
    if (params?.planId) searchParams.append('plan_id', params.planId)
    if (params?.chainId) searchParams.append('chain_id', params.chainId)
    if (params?.operationType) searchParams.append('operation_type', params.operationType)
    if (params?.isActive !== undefined) searchParams.append('is_active', params.isActive ? 'true' : 'false')
    if (params?.limit) searchParams.append('limit', params.limit.toString())

    const query = searchParams.toString()
    const url = query
      ? `/admin/portal/onchain-operation-policy-plan-overrides?${query}`
      : '/admin/portal/onchain-operation-policy-plan-overrides'

    try {
      const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
      const items = extractArrayFromApiPayload<any>(response.data, {
        keys: ['items', 'overrides'],
        candidateKeys: ['items', 'records'],
        includePagination: true,
      })
      return items.map(mapOnchainPlanOverrideRecord)
    } catch (error) {
      console.error('Error fetching on-chain operation policy plan overrides:', error)
      throw error
    }
  },

  async getOnchainOperationPolicyCapabilities(
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyCapability[]> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      '/admin/portal/onchain-operation-policies/capabilities',
      buildModeAwareConfig(mode)
    )
    const payload = response.data?.data ?? response.data
    const items = Array.isArray(payload?.items) ? payload.items : []
    return items.map((record: any) => ({
      chainId: toStringSafe(record?.chainId ?? record?.chain_id) ?? '',
      chainCode: toStringSafe(record?.chainCode ?? record?.chain_code) ?? '',
      compatibility: toStringSafe(record?.compatibility) ?? '',
      operationType: toStringSafe(record?.operationType ?? record?.operation_type) ?? '',
      baselinePolicyId: toStringSafe(record?.baselinePolicyId ?? record?.baseline_policy_id) ?? null,
      baselineActive: toBooleanValue(record?.baselineActive ?? record?.baseline_active, false),
      legacyUnconfiguredAllowed: toBooleanValue(
        record?.legacyUnconfiguredAllowed ?? record?.legacy_unconfigured_allowed,
        false
      ),
      planOverrideConfigurable: toBooleanValue(
        record?.planOverrideConfigurable ?? record?.plan_override_configurable,
        false
      ),
    }))
  },

  async getOnchainOperationPolicyPlanOverride(
    overrideId: string,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyPlanOverride | null> {
    if (!overrideId) {
      return null
    }
    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        `/admin/portal/onchain-operation-policy-plan-overrides/${overrideId}`,
        buildModeAwareConfig(mode)
      )
      const payload = response.data?.data ?? response.data
      const record = payload?.item ?? payload?.data ?? payload
      return record ? mapOnchainPlanOverrideRecord(record) : null
    } catch (error) {
      console.error(`Error fetching on-chain operation policy plan override ${overrideId}:`, error)
      return null
    }
  },

  async updateOnchainOperationPolicyPlanOverride(
    overrideId: string,
    payload: UpdateOnchainOperationPolicyPlanOverridePayload,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyPlanOverride | null> {
    const response = await adminApiClient.put<ApiResponse<any>>(
      `/admin/portal/onchain-operation-policy-plan-overrides/${overrideId}`,
      buildPlanOverridePayload(payload),
      buildModeAwareConfig(mode)
    )
    const responsePayload = response.data?.data ?? response.data
    return mapOnchainPlanOverrideMutationPayload(responsePayload)
  },

  async createOnchainOperationPolicyPlanOverride(
    payload: UpdateOnchainOperationPolicyPlanOverridePayload,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyPlanOverride | null> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/portal/onchain-operation-policy-plan-overrides',
      buildPlanOverridePayload(payload),
      buildModeAwareConfig(mode)
    )
    const responsePayload = response.data?.data ?? response.data
    return mapOnchainPlanOverrideMutationPayload(responsePayload)
  },

  async setOnchainOperationPolicyPlanOverrideActive(
    overrideId: string,
    isActive: boolean,
    expectedRevision: number,
    mode?: NetworkEnvironment
  ): Promise<OnchainOperationPolicyPlanOverride | null> {
    const action = isActive ? 'enable' : 'disable'
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/portal/onchain-operation-policy-plan-overrides/${overrideId}/${action}`,
      { expected_revision: expectedRevision },
      buildModeAwareConfig(mode)
    )
    const responsePayload = response.data?.data ?? response.data
    return mapOnchainPlanOverrideMutationPayload(responsePayload)
  },

  async deleteOnchainOperationPolicyPlanOverride(
    overrideId: string,
    mode?: NetworkEnvironment
  ): Promise<boolean> {
    const response = await adminApiClient.delete<ApiResponse<any>>(
      `/admin/portal/onchain-operation-policy-plan-overrides/${overrideId}`,
      buildModeAwareConfig(mode)
    )
    const payload = response.data?.data ?? response.data
    return Boolean(payload?.deleted ?? true)
  },

  async refreshOnchainOperationPolicyCache(mode?: NetworkEnvironment): Promise<{
    status?: string
    mode?: 'mainnet' | 'testnet'
    refreshed?: number
    failed?: number
    requestedAt?: string
    finishedAt?: string
  }> {
    try {
      const response = await adminApiClient.post<ApiResponse<any>>(
        '/admin/onchain-operation-policies/cache/refresh',
        undefined,
        buildModeAwareConfig(mode)
      )
      return (response.data?.data ?? response.data ?? {}) as {
        status?: string
        mode?: 'mainnet' | 'testnet'
        refreshed?: number
        failed?: number
        requestedAt?: string
        finishedAt?: string
      }
    } catch (error) {
      console.error('Error refreshing on-chain operation policy cache:', error)
      throw error
    }
  },

  async refreshGasPrices(
    params?: { chainIds?: string[] },
    mode?: NetworkEnvironment
  ): Promise<Record<string, any>> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)
    
    try {
      const response = await adminApiClient.post<ApiResponse<Record<string, any>>>(
        '/admin/economy/gas-prices/refresh',
        params || {},
        { signal: controller.signal, ...buildModeAwareConfig(mode) }
      )

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from gas price refresh API')
      }

      return response.data.data
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Gas price refresh timeout after 15 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },
}
